import { useState, useEffect, useMemo } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { updateCompanyResource } from '../../graphql/companyResourceQueries';
import type { TrainingTopic } from '../../types/trainingTypes';

interface DeleteTopicModalProps {
    isOpen: boolean;
    topic: TrainingTopic | null;
    onClose: () => void;
    onDeleted: () => void;
}

/**
 * Delete Topic Modal — styled confirmation dialog for deleting a training topic.
 * If the topic is assigned to an active plan (scheduled or not), deletion is blocked
 * and an informational message is shown instead.
 */
export const DeleteTopicModal = ({ isOpen, topic, onClose, onDeleted }: DeleteTopicModalProps) => {
    const { plans, schedules, deleteTopic } = useSafetyTrainingContext();
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (isOpen) setDeleting(false);
    }, [isOpen]);

    // Determine which active plans this topic belongs to
    const activePlanInfo = useMemo(() => {
        if (!topic) return [];
        const activePlans = plans.filter(p => p.status === 'ACTIVE');
        const info: { id: string; name: string; isScheduled: boolean }[] = [];

        for (const plan of activePlans) {
            const isAssigned = topic.planIds?.includes(plan.id) ?? false;
            const isScheduled = schedules.some(s => s.planId === plan.id && s.topicId === topic.id);
            if (isAssigned || isScheduled) {
                info.push({ id: plan.id, name: plan.name, isScheduled });
            }
        }
        return info;
    }, [topic, plans, schedules]);

    const isProtected = activePlanInfo.length > 0;

    // All plan assignments (for display)
    const assignedPlans = useMemo(() => {
        if (!topic) return [];
        return (topic.planIds || [])
            .map(pid => plans.find(p => p.id === pid))
            .filter(Boolean)
            .map((p: any) => ({ id: p.id, name: p.name, status: p.status }));
    }, [topic, plans]);

    const isUnassigned = assignedPlans.length === 0;

    if (!isOpen || !topic) return null;

    const handleDelete = async () => {
        if (!topic || isProtected) return;
        setDeleting(true);
        try {
            await deleteTopic(topic.id);

            if (topic.companyResourceId) {
                try {
                    await updateCompanyResource({ id: topic.companyResourceId, trainingTopicId: null } as any);
                } catch (err) {
                    console.error('Failed to unlink Company Resource:', err);
                }
            }

            onDeleted();
        } catch (err) {
            console.error('Error deleting topic:', err);
        } finally {
            setDeleting(false);
        }
    };

    // Protected state — informational modal, no delete allowed
    if (isProtected) {
        return (
            <div className="cr-modal-overlay" onClick={onClose}>
                <div className="cr-modal" onClick={e => e.stopPropagation()}>
                    <div className="cr-modal-header">
                        <h2 className="cr-modal-title">Cannot Delete Topic</h2>
                        <button className="cr-modal-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="cr-modal-body">
                        <p style={{ fontSize: '0.9rem', color: '#374151' }}>
                            <strong>"{topic.topicId}"</strong> cannot be deleted because it is assigned to an active training plan.
                        </p>

                        <div style={{ margin: '12px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                            <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#991b1b', margin: '0 0 8px' }}>
                                🚫 Active plan assignment{activePlanInfo.length > 1 ? 's' : ''}:
                            </p>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.84rem', color: '#7f1d1d' }}>
                                {activePlanInfo.map(p => (
                                    <li key={p.id} style={{ marginBottom: 4 }}>
                                        <strong>{p.name}</strong>
                                        {p.isScheduled ? (
                                            <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#dc2626' }}>
                                                — scheduled in this plan
                                            </span>
                                        ) : (
                                            <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#92400e' }}>
                                                — assigned but not yet scheduled
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div style={{ margin: '12px 0', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', lineHeight: 1.5 }}>
                            To remove this topic, first unassign it from the active plan(s) using the plan's schedule builder, or deactivate the plan.
                        </div>
                    </div>
                    <div className="cr-modal-footer">
                        <button className="cr-btn cr-btn--secondary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    // Normal delete flow — topic is not in any active plan
    return (
        <div className="cr-modal-overlay" onClick={onClose}>
            <div className="cr-modal" onClick={e => e.stopPropagation()}>
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">Delete Topic</h2>
                    <button className="cr-modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="cr-modal-body">
                    <p style={{ fontSize: '0.9rem', color: '#374151' }}>
                        Are you sure you want to delete <strong>"{topic.topicId}"</strong>?
                        {' '}This action cannot be undone.
                    </p>

                    {/* Plan assignment info (draft/completed only at this point) */}
                    {isUnassigned ? (
                        <div style={{ margin: '12px 0', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#64748b' }}>
                            This topic is not assigned to any training plan.
                        </div>
                    ) : (
                        <div style={{ margin: '12px 0', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                            <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e', margin: '0 0 6px' }}>
                                📋 Assigned to {assignedPlans.length} plan{assignedPlans.length !== 1 ? 's' : ''}:
                            </p>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.84rem', color: '#78350f' }}>
                                {assignedPlans.map(p => (
                                    <li key={p.id} style={{ marginBottom: 2 }}>
                                        {p.name}
                                        <span style={{
                                            marginLeft: 8,
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            background: p.status === 'DRAFT' ? '#fef9c3' : '#e2e8f0',
                                            color: p.status === 'DRAFT' ? '#854d0e' : '#475569',
                                        }}>
                                            {p.status}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Linked Company Resource info */}
                    {topic.companyResourceId && (
                        <div style={{ margin: '12px 0', padding: '12px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                            <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0c4a6e', margin: '0 0 4px' }}>
                                🔗 This topic is linked to a Company Resource
                            </p>
                            <p style={{ fontSize: '0.82rem', color: '#475569', margin: 0 }}>
                                Deleting this topic will unlink it from the resource. To delete both, use the Company Resources page.
                            </p>
                        </div>
                    )}
                </div>
                <div className="cr-modal-footer">
                    <button className="cr-btn cr-btn--secondary" onClick={onClose} disabled={deleting}>Cancel</button>
                    <button
                        className="cr-btn cr-btn--danger-solid"
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete Topic'}
                    </button>
                </div>
            </div>
        </div>
    );
};
